import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { Button, Icon, tokens, Accordion, Text } from '@hotjar/hotsauce';
import type { OrganizationModel, IdentityProviderModel } from '@hotjar/webapp/domains/sso/model';
import { useCurrentUser } from '@hotjar/user';
import { useOrganizations } from '@hotjar/organization';

import { OrganizationItem } from './OrganizationItem';

interface Props {
  identityProvider: IdentityProviderModel;
  onConnectOrganizationClick: (identityProvider: IdentityProviderModel) => void;
  onDisconnectOrganizationClick: (selectedOrganization: OrganizationModel) => void;
}

const Row = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  & + & {
    margin-top: ${tokens.spacer.x3};
  }
`;

const IconColumn = styled.div`
  margin-right: ${tokens.spacer.x2};
`;

const Caption = styled.span`
  ${tokens.text('caption')};

  color: ${tokens.color.text_light};
  display: block;
  margin-bottom: ${tokens.spacer.x0};
`;

const Value = styled(Text)`
  display: inline-flex;
  align-items: center;
`;

const OrgRow = styled.div`
  width: 100%;
`;

export const IdentityProviderListItem = ({
  identityProvider,
  onConnectOrganizationClick,
  onDisconnectOrganizationClick,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const { data: organizations } = useOrganizations(user?.id);

  return (
    <Accordion.Section
      title={t('common:settings.sso.list-identity-providers.login-id')}
      description={identityProvider.loginId}
      icon="lock"
      data-testid={`identity-provider-${identityProvider.id}`}
    >
      <Row>
        <IconColumn>
          <Icon icon="building_agency" size="l" />
        </IconColumn>
        <OrgRow>
          <Caption as="span">
            {t('common:settings.sso.list-identity-providers.connected-organizations')}
          </Caption>

          {identityProvider.organizations.length ? (
            identityProvider.organizations.map((organization) => (
              <OrganizationItem
                key={organization.id}
                organization={organization}
                onClick={() => onDisconnectOrganizationClick(organization)}
              />
            ))
          ) : (
            <Value as="span" textType="subtitle2" textColor="text_light">
              {t('common:settings.sso.list-identity-providers.no-connected-organizations')}
            </Value>
          )}
        </OrgRow>
      </Row>

      {!!organizations?.length && (
        <Row>
          <Button
            icon="plus"
            kind="primary"
            onClick={() => onConnectOrganizationClick(identityProvider)}
            data-testid="list-identity-providers.connect-organization"
          >
            {t('common:settings.sso.list-identity-providers.connect-organization')}
          </Button>
        </Row>
      )}
    </Accordion.Section>
  );
};
